import { memo } from 'react';
import clsx from 'clsx';
import { Avatar, Chip, Grid } from '@material-ui/core';
import useStyles from './styles';
import * as assets from '@assets';
import { CustomizedTypography, Stack } from '@components/Customized';
import { Image } from '@components/Image';
import { colorSet, useSnackbar } from '@components/Provider';
import { formatCurrency, timeForTodayKTC, errorMessage } from '@utils';
import {
  CapaQuotationMyReceivedQuotation,
  CapaQuotationPartnerStatus,
  CapaRfqReceivedType,
  FetchQuotationUserRfqDetailDocument,
  FetchQuotationUserRfqDetailQueryResult,
  FetchQuotationUserRfqsQueryVariables,
  FetchQuotationUserRfqsDocument,
  FetchQuotationUserRfqsQuery,
  useReadRfqQuotationMutation,
  FetchQuotationUserRfqDetailQueryVariables,
  CapaPurchaseOrderStatus
} from '@generated/graphql';
import AppConfig from '@config';
import { onClickCardParams } from '../types';
import { QuotationType } from '@modules/dashboard';
import { useReactiveVar } from '@apollo/client';
import { paginationVar } from '../RequestListProvider';
import { apolloClient } from '@api/apollo';
import PurchaseOrderBadge from './PurchaseOrderBadge';
import { StarFilled } from '@carbon/icons-react';
import { useSelector } from '@store';

const PROCESS_STOPPED_STATUS: (CapaPurchaseOrderStatus | undefined | null)[] = [
  CapaPurchaseOrderStatus.CancelConfirmed,
  CapaPurchaseOrderStatus.CancelRequested,
  CapaPurchaseOrderStatus.NotPaidCanceled,
  CapaPurchaseOrderStatus.PaymentFailed,
  CapaPurchaseOrderStatus.RefundConfirmed,
  CapaPurchaseOrderStatus.RefundRequested,
  CapaPurchaseOrderStatus.RejectOrderByPartner,
  CapaPurchaseOrderStatus.CancelledByAdmin
];

export const CAN_NOT_TRAKING_STATUS: (
  | CapaPurchaseOrderStatus
  | undefined
  | null
)[] = [
  CapaPurchaseOrderStatus.Paid,
  CapaPurchaseOrderStatus.CancelRequested,
  CapaPurchaseOrderStatus.RefundRequested,
  CapaPurchaseOrderStatus.PurchaseConfirmed,
  CapaPurchaseOrderStatus.Shipping,
  CapaPurchaseOrderStatus.Created
];

interface PartnerCardProps {
  quotation?: CapaQuotationMyReceivedQuotation;
  isPurchaseOrder?: boolean;
  onClickCard?: (params: onClickCardParams) => void;
  refetchAccordion: () => void;
}

const PartnerCard = ({
  quotation,
  isPurchaseOrder,
  onClickCard,
  refetchAccordion
}: PartnerCardProps) => {
  const { page, limit } = useReactiveVar(paginationVar);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const selectedQuotationId = useSelector(
    (state) => state?.dashboard?.selectedQuotationId
  );
  const [quotationReadTrigger] = useReadRfqQuotationMutation({
    variables: {
      input: {
        rfqId: quotation?.rfqId ?? '',
        partnerId: quotation?.partnerId ?? ''
      }
    },
    fetchPolicy: 'no-cache'
  });

  if (!quotation) return null;

  const { purchaseOrderStatus } = quotation;

  const rfqListRefetchHandler = async () => {
    await apolloClient.query<
      FetchQuotationUserRfqsQuery,
      FetchQuotationUserRfqsQueryVariables
    >({
      fetchPolicy: 'network-only',
      query: FetchQuotationUserRfqsDocument,
      variables: {
        input: {
          offset: 0,
          limit: page * limit
        }
      }
    });
  };

  const handleQuotationRead = () => {
    if (quotation?.read) return;

    quotationReadTrigger();
    refetchAccordion();
    rfqListRefetchHandler();
  };

  const refetchRfqDetail = async () => {
    if (!quotation.rfqId) {
      return enqueueSnackbar(errorMessage.service, { variant: 'error' });
    }

    const { data } = await apolloClient.query<
      FetchQuotationUserRfqDetailQueryResult,
      FetchQuotationUserRfqDetailQueryVariables
    >({
      query: FetchQuotationUserRfqDetailDocument,
      variables: { rfqId: quotation.rfqId },
      fetchPolicy: 'cache-first'
    });

    if (!data) {
      enqueueSnackbar(errorMessage.service, { variant: 'error' });
    }
  };

  const onSelectQuotation = async () => {
    handleQuotationRead();

    await refetchRfqDetail();

    const isTraking = !CAN_NOT_TRAKING_STATUS.includes(purchaseOrderStatus);

    onClickCard &&
      onClickCard({
        rfqId: quotation.rfqId ?? '',
        chatRoomId: quotation?.chatRoomId ?? '',
        partnerId: quotation?.partnerId ?? '',
        quotationId: quotation?.quotationId
          ? quotation?.quotationId
          : undefined,
        quotationType: quotation?.quotationType as QuotationType,
        eventType: 'QUOTATION',
        ordered: !!quotation?.isTransaction && !!isPurchaseOrder,
        isPurchaseOrder: !!isPurchaseOrder,
        isOnlyIntroduction:
          quotation?.rfqReceivedType === CapaRfqReceivedType.OnlyIntroduction,
        chatRoomStatus: quotation?.chatRoomStatus ?? undefined,
        isTraking
      });
  };

  const isWithdrawn =
    quotation?.quotationPartnerStatus === CapaQuotationPartnerStatus.Deleted ||
    quotation?.quotationPartnerStatus === null;

  const isProcessStarted =
    (quotation?.isTransaction ?? false) && Boolean(purchaseOrderStatus);
  const isOnProcess = !PROCESS_STOPPED_STATUS.includes(purchaseOrderStatus);

  return (
    <div onClick={onSelectQuotation}>
      <Grid
        container
        className={clsx(classes.root, {
          active: selectedQuotationId?.quotationId === quotation?.quotationId
        })}
        wrap="nowrap"
      >
        <PurchaseOrderBadge
          visible={isProcessStarted}
          isOnProcess={isOnProcess}
        />
        <CustomizedTypography className={classes.createdAt}>
          {timeForTodayKTC(
            quotation.createdAt ??
              quotation.chatRoomCreatedAt ??
              new Date().toString(),
            'Notification'
          )}
        </CustomizedTypography>
        <Grid item className={classes.avatar}>
          <Avatar>
            <Image
              src={
                quotation?.profileId
                  ? `${AppConfig.fileApiUrl}/file/${quotation?.profileId}`
                  : assets.DefaultProfile
              }
              onError={(e) => {
                e.currentTarget.src = assets.DefaultProfile;
              }}
              alt=""
            />
          </Avatar>
        </Grid>
        <Grid item className={classes.partner}>
          <Stack gap={8} alignItems="center">
            <CustomizedTypography variant="body2" fontWeight="500">
              {quotation?.organizationName}
            </CustomizedTypography>

            <Stack gap={4} alignItems="center" margin={'0px 0px 4px 0px'}>
              <StarFilled color={colorSet.primary500} size={16} />
              {/* 별점, 후기 없는경우 0.0 (0)으로 표기  - 영 (2023. 3. 8) */}
              <CustomizedTypography variant="body2" color="textSecondary">
                {quotation?.satisfactioPoint
                  ? quotation.satisfactioPoint.toFixed(1)
                  : (0).toFixed(1)}{' '}
                ({quotation?.reviewCnt || 0})
              </CustomizedTypography>
            </Stack>
          </Stack>

          <div className={classes.cost}>
            <CustomizedTypography
              className={clsx('quotation', {
                'withdraw': isWithdrawn
              })}
              variant="body2"
              fontWeight="700"
            >
              {!quotation?.quotationType
                ? '상담후 협의 '
                : `${formatCurrency(quotation?.cost ? quotation?.cost : 0)} 원`}
              {quotation.rfqReceivedType ===
                CapaRfqReceivedType.OnlyIntroduction && '(회사소개서만 수령함)'}
            </CustomizedTypography>
            <div className={classes.labelBlock}>
              {!quotation?.read && quotation?.read !== null && (
                <Chip label="N" className={classes.newChip} />
              )}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(PartnerCard);
